import { Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { combineLatest, filter, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AnnotationState } from '../../../../../cross-team-shared/annotation/shared/state/annotation-state';
import { areTagsStatesEqual } from '../../../../../cross-team-shared/misc-services/tag-helper';
import { VisibilityState } from '../../../../../team-apis/otterdam/answers/shape-typings';
import { ObjectVisibilityService } from '../../../../shared/misc-services/object-visibility.service';
import { getVisibilityState } from '../../../../shared/utils/shape-visibility-utils';
import { FocusedObjectService } from './focused-object.service';
import { ObjectlessFocusedShapeService } from './objectless-focused-shape.service';

/**
 * This service acts as a single point access to focused shape or object.
 * Focused here means only one selected,
 * while none selected or multiple selected means not focused.
 */
@Injectable()
export class FocusedAnnotationService {
  constructor(
    private focusedShapeService: ObjectlessFocusedShapeService,
    private focusedObjectService: FocusedObjectService,
    private objectVisibilityService: ObjectVisibilityService,
  ) {}

  getFocusedTags$() {
    return this.getAnnotationStore$().pipe(
      switchMap((annotationStore) => {
        if (annotationStore) {
          return annotationStore.$;
        }
        return of(undefined);
      }),
      distinctUntilChanged(
        (x, y) => isDefined(x) && isDefined(y) && areTagsStatesEqual(x, y),
      ),
    );
  }

  getLocations$() {
    return this.getAnnotationStores$().pipe(
      switchMap(([shapeStore, objectStore]) => {
        if (objectStore || !shapeStore) {
          return of({ key_locations: [] });
        }
        return shapeStore.$;
      }),
    );
  }

  getAnnotationStore$(): Observable<Store<AnnotationState> | undefined> {
    return this.getAnnotationStores$().pipe(
      map(([shapeStore, objectStore]) => objectStore ?? shapeStore),
    );
  }

  getVisibility$(frameNumber: number) {
    return this.getAnnotationStores$().pipe(
      switchMap(([shapeStore, objectStore]) => {
        if (shapeStore) {
          return shapeStore.$.pipe(
            filter(isDefined),
            map((shape) => getVisibilityState(shape, frameNumber)),
          );
        }
        if (objectStore) {
          return this.objectVisibilityService.getVisibilityState(
            // @ts-ignore -- fix me
            objectStore,
            frameNumber,
          );
        }
        return of(0 as VisibilityState);
      }),
    );
  }

  getAnnotationStores$() {
    return combineLatest([
      this.focusedShapeService.getFocusedShapeStore$(),
      this.focusedObjectService.getFocusedObjectStore$(),
    ]);
  }

  getFocusedAnnotationId$() {
    return this.getAnnotationStore$().pipe(
      switchMap((annotationStore) => {
        if (annotationStore) {
          return annotationStore('index').$;
        }
        return of(undefined);
      }),
    );
  }
}
